import { PlusOutlined } from '@ant-design/icons';
import { message } from 'antd';
import type { FC } from 'react';
import { useState } from 'react';

import type { Picture } from 'app/components/commons/Uploader/Types';
import { SortableUploadList } from 'app/components/commons/Uploader/UploadList/UploadList';
import { GalleryModal } from 'app/components/pages/Galleries/GalleryModal/GalleryModal';

import './Pictures.scss';

const DEFAULT_PICTURES_CATEGORIES = [{ name: 'Sourcing', type: 'sourcing' }];

export const PicturesComponent: FC<Props> = ({
  value,
  onChange,
  roomCategoryId,
  categories,
  hotelId,
  picturesCategories = DEFAULT_PICTURES_CATEGORIES,
  kind = 'sourcing',
  previewable = false,
  removable = false,
  draggable = false,
  disabled = false,
}) => {
  const [internalValue, setInternalValue] = useState(value || []);
  const [modalIsVisible, setModalIsVisible] = useState(false);
  const pictures = internalValue;

  if (!onChange) {
    return null;
  }

  const handleOnChange = (newValue: Picture[]) => {
    setInternalValue(newValue);
    onChange(newValue);
  };

  const addPictures = (newPictures: Picture[]) => {
    const existingIds = new Set(pictures.map((pic) => pic.id));
    const uniquePictures = newPictures.filter(
      (pic) => !existingIds.has(pic.id)
    );

    if (uniquePictures.length < newPictures.length) {
      message.warning(
        newPictures.length > 1
          ? 'One of your images could not be added because it is already in your selection.'
          : 'This image is already in your selection.'
      );
    }

    if (!uniquePictures.length) {
      return;
    }

    handleOnChange([...pictures, ...uniquePictures]);
  };

  const handleRemovePicture = (index: number) => {
    if (index >= 0) {
      handleOnChange([
        ...pictures.slice(0, index),
        ...pictures.slice(index + 1),
      ]);
    }
  };

  const onRemove = (uid: string) => {
    const index = pictures.findIndex(
      (pic) => uid === `${pic.pictureId}-${pic.id}`
    );

    handleRemovePicture(index);
  };

  return (
    <div className="pictures-content">
      <SortableUploadList
        pictures={pictures || []}
        uploadingItems={{}}
        onChange={handleOnChange}
        remove={removable ? onRemove : undefined}
        customButton={
          <div onClick={() => setModalIsVisible(true)}>
            <PlusOutlined />
            <span className="text">Add</span>
          </div>
        }
        previewable={previewable}
        draggable={draggable}
        disabled={disabled}
      />
      <GalleryModal
        visible={modalIsVisible}
        title={roomCategoryId ? categories[roomCategoryId] : 'New room'}
        onOk={(newPictures) => {
          addPictures(newPictures as unknown as Picture[]);
          setModalIsVisible(false);
        }}
        onCancel={() => setModalIsVisible(false)}
        hotelId={hotelId}
        okText="Save"
        cancelText="Cancel"
        categories={picturesCategories}
        subTitle="Select photos in your media gallery or upload new ones to illustrate this space"
        kind={kind}
      />
    </div>
  );
};

type Props = {
  value?: Picture[];
  onChange?: (pictures: Picture[]) => void;
  roomCategoryId?: number;
  categories: { [id: number]: string };
  hotelId: number;
  picturesCategories?: { name: string; type: string }[];
  kind?: string;
  previewable?: boolean;
  removable?: boolean;
  draggable?: boolean;
  disabled?: boolean;
};
